/**
 * SquaresModal — iter87.550. Player-facing Squares surface: status +
 * self opt-in/out, the "Luck of the Draw" explainer (auto mode), or
 * the 10x10 box-picking grid (manual mode) with a floating footer
 * running total. Mirrors TrophyTokensLegendModal's shell + the
 * MatchdayBoostModal pattern of a page-level hook feeding the tile.
 */
import { useCallback, useEffect, useState } from "react";
import { X, Grid3x3, Lock, LogOut, LogIn, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function initialsOf(name) {
    return (name || "").trim().slice(0, 2).toUpperCase() || "—";
}

// iter87.553 — Cheer Section results grid highlight. `wins` is the list
// of checkpoint keys (q1/q2/q3/final) a box won; Final gets a gold glow,
// any quarter win gets a cyan ring. A box can carry both.
function winnerRingCls(wins) {
    if (!wins || wins.length === 0) return "";
    if (wins.includes("final")) return "border-2 border-amber-300 shadow-[0_0_8px_-1px_rgba(252,211,77,0.85)]";
    return "border-2 border-cyan-400";
}

function SquaresGridRow({ r, byIdx, digits, meId, onToggle, busyBox, locked, winnersByIdx, highlightMine, testIdPrefix }) {
    return (
        <>
            <div className="text-center text-[10px] font-bold text-heat flex items-center justify-center">
                {digits ? digits.away[r] : "?"}
            </div>
            {Array.from({ length: 10 }).map((_, c) => {
                const idx = r * 10 + c;
                const box = byIdx[idx] || { box_index: idx, participant_id: null, username: null };
                const mine = !!meId && box.participant_id === meId;
                const taken = !!box.participant_id;
                const wins = winnersByIdx?.[idx];
                const winCls = winnerRingCls(wins);
                // iter87.553.1 (testing-agent fix) — in the Cheer Section
                // results grid, "mine" must NOT use the same border
                // treatment as the win rings (looked identical to the
                // cyan quarter-winner ring at a glance) — keep ownership
                // as a background tint only there, border is reserved
                // for the win ring.
                return (
                    <button
                        key={idx}
                        type="button"
                        disabled={busyBox === idx || locked || (taken && !mine)}
                        onClick={() => onToggle(box)}
                        data-testid={`${testIdPrefix}-box-${idx}`}
                        title={box.username || "Open"}
                        className={`w-[27px] h-[27px] rounded-sm border text-[8px] font-bold flex items-center justify-center transition ${
                            mine && highlightMine
                                ? "border-2 border-volt bg-volt/20 text-volt"
                                : taken
                                    ? `bg-zinc-800 text-zinc-400 ${mine ? "ring-1 ring-inset ring-volt/50 bg-volt/10" : "border-zinc-700"}`
                                    : "border-zinc-700 bg-zinc-900 text-zinc-600 hover:border-volt/50 hover:text-volt"
                        } ${busyBox === idx ? "opacity-50" : ""} ${winCls}`}
                    >
                        {taken ? initialsOf(box.username) : ""}
                    </button>
                );
            })}
        </>
    );
}

function SquaresGrid({ digits, boxes, meId, onToggle, busyBox, locked, winnersByIdx, highlightMine = true, testIdPrefix = "squares" }) {
    const byIdx = {};
    boxes.forEach((b) => { byIdx[b.box_index] = b; });
    return (
        <div className="overflow-x-auto" data-testid={`${testIdPrefix}-grid`}>
            <div className="inline-grid gap-[3px]" style={{ gridTemplateColumns: "20px repeat(10, 27px)" }}>
                <div />
                {Array.from({ length: 10 }).map((_, c) => (
                    <div key={`h-${c}`} className="text-center text-[10px] font-bold text-cyber">
                        {digits ? digits.home[c] : "?"}
                    </div>
                ))}
                {Array.from({ length: 10 }).map((_, r) => (
                    <SquaresGridRow key={r} r={r} byIdx={byIdx} digits={digits} meId={meId} onToggle={onToggle} busyBox={busyBox} locked={locked} winnersByIdx={winnersByIdx} highlightMine={highlightMine} testIdPrefix={testIdPrefix} />
                ))}
            </div>
        </div>
    );
}

const STATUS_LABEL = {
    confirmed: "Confirmed", opted_in: "Opted in · awaiting confirm",
    dropped: "Opted out", none: "Opted in · awaiting confirm",
};

export default function SquaresModal({ open, onOpenChange, tournament, me, squaresState, onRefresh }) {
    const tid = tournament?.id;
    const meId = me?.id;
    const [board, setBoard] = useState(null);
    const [checkpointsData, setCheckpointsData] = useState(null);
    const [roster, setRoster] = useState([]);
    const [busyBox, setBusyBox] = useState(null);
    const [busyOpt, setBusyOpt] = useState(false);

    const loadBoard = useCallback(async () => {
        if (!tid || !open) return;
        try {
            const data = await api.squaresGetBoard(tid);
            setBoard(data);
        } catch (e) { /* best-effort */ }
    }, [tid, open]);

    // iter87.551 — live, display-only checkpoint results (never moves
    // tokens itself — Finalize is an admin-tab-only action).
    const loadCheckpoints = useCallback(async () => {
        if (!tid || !open) return;
        try {
            const data = await api.squaresGetCheckpoints(tid);
            setCheckpointsData(data);
        } catch (e) { /* best-effort */ }
    }, [tid, open]);

    useEffect(() => {
        if (open) { loadBoard(); loadCheckpoints(); onRefresh?.(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, loadBoard, loadCheckpoints]);

    // iter87.553.1 (testing-agent fix) — the Cheer Section leaderboard
    // needs every CONFIRMED player, including anyone who claimed boxes
    // but won 0 tokens (the ledger only has rows for actual winners),
    // so load the roster once the board is finalized.
    useEffect(() => {
        if (!tid || !open || !checkpointsData?.finalized) return;
        api.squaresGetRoster(tid).then((d) => setRoster(d.roster || [])).catch(() => {});
    }, [tid, open, checkpointsData?.finalized]);

    // iter87.551 fix (testing-agent caught) — Live Results never
    // refreshed while the modal stayed open during a live game; poll
    // every 45s while it's open, locked, and the game isn't final yet.
    useEffect(() => {
        if (!open || !checkpointsData?.locked || checkpointsData?.game_finished) return undefined;
        const id = setInterval(loadCheckpoints, 45000);
        return () => clearInterval(id);
    }, [open, checkpointsData?.locked, checkpointsData?.game_finished, loadCheckpoints]);

    if (!open || !tournament || !me) return null;
    const st = squaresState || {};

    // iter87.550 fix (testing-agent caught) — the tile/hook already
    // reads pro_plus_ok off this same config payload; the modal must
    // honor it too instead of rendering a full pick UI that 402s on
    // every action for a non-Pro+ league.
    if (st.pro_plus_ok === false) {
        return (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
                data-testid="squares-modal-pro-gate-backdrop"
                onClick={(e) => { if (e.target === e.currentTarget) onOpenChange?.(false); }}
            >
                <div className="brutal-card max-w-md w-full p-5" data-testid="squares-modal-pro-gate" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <h2 className="font-display font-black text-lg uppercase text-white">Squares</h2>
                        <button onClick={() => onOpenChange?.(false)} className="brutal-btn-ghost p-1.5" data-testid="squares-modal-pro-gate-close-btn" aria-label="Close Squares">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <p className="text-sm text-zinc-400">Squares is a Pro+ subscriber feature. Ask your rivalry admin to upgrade to unlock it.</p>
                </div>
            </div>
        );
    }

    const mode = board?.mode || st.mode || "manual";
    const sport = (board?.sport || st.sport || "nfl").toUpperCase();
    const game = board?.game || st.game;
    const cfg = board?.config || st.config || {};
    const locked = board?.locked ?? st.locked ?? false;
    const myState = st.my_state || "opted_in";
    const boxes = board?.boxes || Array.from({ length: 100 }, (_, i) => ({ box_index: i, participant_id: null, username: null }));
    const myBoxCount = boxes.filter((b) => b.participant_id === meId).length;
    const cap = cfg.max_squares_per_player || 0;
    const atCap = cap > 0 && myBoxCount >= cap;
    const costCopy = cfg.allocation_basis === "per_player" ? `${cfg.amount} tokens flat` : `${cfg.amount} tokens / box`;
    const committedTotal = cfg.allocation_basis === "per_player" ? cfg.amount : (cfg.amount || 0) * myBoxCount;

    // iter87.553 — Cheer Section: winning boxes per checkpoint (for the
    // results grid highlight) + a token leaderboard, both derived from
    // the same `checkpointsData` the Live Results card already polls.
    const winnersByIdx = {};
    if (checkpointsData?.finalized) {
        Object.entries(checkpointsData.checkpoints || {}).forEach(([cp, r]) => {
            if (r.ready && r.box_index != null) {
                (winnersByIdx[r.box_index] = winnersByIdx[r.box_index] || []).push(cp);
            }
        });
    }
    const leaderboard = (() => {
        if (!checkpointsData?.finalized) return [];
        const usernameById = {};
        boxes.forEach((b) => { if (b.participant_id) usernameById[b.participant_id] = b.username; });
        roster.forEach((r) => { usernameById[r.id] = r.username; });
        const totals = {};
        roster.filter((r) => r.state === "confirmed").forEach((r) => { totals[r.id] = 0; });
        (checkpointsData.ledger || []).forEach((a) => {
            totals[a.participant_id] = (totals[a.participant_id] || 0) + a.tokens_awarded;
        });
        const rows = Object.entries(totals)
            .map(([pid, tokens]) => ({ participant_id: pid, username: usernameById[pid] || pid.slice(0, 8), tokens }))
            .sort((a, b) => b.tokens - a.tokens);
        // iter87.553.1 (testing-agent fix) — tied token totals share the
        // same rank number instead of incrementing sequentially.
        let rank = 0;
        let prevTokens = null;
        return rows.map((row, i) => {
            if (row.tokens !== prevTokens) { rank = i + 1; prevTokens = row.tokens; }
            return { ...row, rank };
        });
    })();

    const doSelfOptOut = async () => {
        setBusyOpt(true);
        try {
            await api.squaresSelfOptOut(tid, meId);
            toast.success("You're opted out of Squares");
            onRefresh?.(); await loadBoard();
        } catch (err) { toast.error(err.response?.data?.detail || "Couldn't opt out"); }
        setBusyOpt(false);
    };
    const doSelfOptIn = async () => {
        setBusyOpt(true);
        try {
            await api.squaresSelfOptIn(tid, meId);
            toast.success("You're opted back in");
            onRefresh?.(); await loadBoard();
        } catch (err) { toast.error(err.response?.data?.detail || "Couldn't opt in"); }
        setBusyOpt(false);
    };

    const toggleBox = async (box) => {
        if (locked || mode !== "manual") return;
        if (myState !== "confirmed") { toast.error("Ask your rivalry admin to confirm you first"); return; }
        setBusyBox(box.box_index);
        try {
            if (box.participant_id === meId) {
                await api.squaresReleaseBox(tid, meId, box.box_index);
            } else if (!box.participant_id) {
                if (atCap) { toast.error(`You've hit your ${cap}-box limit`); setBusyBox(null); return; }
                await api.squaresClaimBox(tid, meId, box.box_index);
            } else {
                setBusyBox(null); return;
            }
            await loadBoard(); onRefresh?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "That didn't work");
            // iter87.550 fix (testing-agent caught) — a 409 collision or a
            // 423 (pool locked mid-session) left the grid stale, still
            // showing the box as open/clickable. Reload so it corrects.
            await loadBoard();
        }
        setBusyBox(null);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-start lg:items-center justify-center p-0 lg:p-4 bg-black/80"
            data-testid="squares-modal-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onOpenChange?.(false); }}
        >
            <div className="brutal-card w-full lg:max-w-2xl h-[100dvh] lg:h-auto lg:max-h-[88dvh] flex flex-col" data-testid="squares-modal" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-3 p-4 border-b-2 border-[#222] shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <Grid3x3 className="w-5 h-5 text-volt shrink-0" />
                        <h2 className="font-display font-black text-lg uppercase truncate text-white">
                            {sport} {mode === "auto" ? "Luck of the Draw" : "Squares"}
                        </h2>
                        {locked && <Lock className="w-3.5 h-3.5 text-blaze shrink-0" />}
                    </div>
                    <button onClick={() => onOpenChange?.(false)} className="brutal-btn-ghost p-1.5" data-testid="squares-modal-close-btn" aria-label="Close Squares">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {game && (
                        <div className="rounded-lg border border-white/10 bg-ink p-3 text-sm" data-testid="squares-modal-game">
                            <div className="font-bold text-white">{game.away_name} @ {game.home_name}</div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">
                                {game.scheduled_at ? new Date(game.scheduled_at).toLocaleString() : "Kickoff TBD"}
                                {locked ? " · Locked" : " · Picks open until kickoff"}
                            </div>
                        </div>
                    )}

                    <div className="rounded-lg border border-white/10 bg-ink p-3 flex items-center justify-between gap-3" data-testid="squares-modal-status">
                        <div className="text-sm min-w-0">
                            <div className="text-[10px] uppercase tracking-wide text-zinc-500">Your status</div>
                            <div
                                className={`font-bold ${myState === "confirmed" ? "text-volt" : myState === "dropped" ? "text-zinc-500" : "text-heat"}`}
                                data-testid="squares-my-status"
                            >
                                {STATUS_LABEL[myState] || myState}
                            </div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">Buy-in: {costCopy}</div>
                        </div>
                        {!locked && myState !== "confirmed" && (
                            myState === "dropped" ? (
                                <button onClick={doSelfOptIn} disabled={busyOpt} data-testid="squares-self-optin-btn"
                                    className="brutal-btn-ghost text-xs font-bold uppercase px-3 py-2 flex items-center gap-1.5 shrink-0">
                                    <LogIn className="w-3.5 h-3.5" /> Opt In
                                </button>
                            ) : (
                                <button onClick={doSelfOptOut} disabled={busyOpt} data-testid="squares-self-optout-btn"
                                    className="brutal-btn-ghost text-xs font-bold uppercase px-3 py-2 flex items-center gap-1.5 shrink-0 border-blaze/40 text-blaze">
                                    <LogOut className="w-3.5 h-3.5" /> Not Interested
                                </button>
                            )
                        )}
                    </div>

                    {mode === "auto" && (
                        <div className="rounded-lg border border-cyber/40 bg-cyber/[0.06] p-3 text-[12px] text-zinc-300 leading-relaxed" data-testid="squares-luck-card">
                            <p className="font-bold text-cyber mb-1 uppercase text-[11px] tracking-wide">How this works</p>
                            <p>
                                Boxes are auto-assigned — no picking needed. {locked ? "The board has locked and" : "The moment the board locks (kickoff),"} all 100
                                boxes are randomly and evenly split among every Confirmed crewmate. Payout is{" "}
                                {cfg.payout_type === "wta" ? "Winner Take All on the final score." : "split across Q1/Q2/Q3/Final checkpoints."}
                            </p>
                            {locked && myBoxCount > 0 && (
                                <p className="mt-2 text-white font-bold" data-testid="squares-my-box-count">
                                    You were dealt {myBoxCount} box{myBoxCount === 1 ? "" : "es"} — see them highlighted below.
                                </p>
                            )}
                        </div>
                    )}
                    {(mode === "manual" || (mode === "auto" && locked)) && (
                        <div className="rounded-lg border border-white/10 bg-ink p-3" data-testid="squares-grid-card">
                            <p className="text-[11px] text-zinc-500 mb-2">
                                {locked ? "Board locked — box owners are shown on the grid." : "Tap an open box to claim it, tap your own box to release it."}
                            </p>
                            <div className="flex items-center justify-center gap-4 text-[10px] font-bold uppercase mb-1.5">
                                <span className="text-cyber">Columns: {game?.home_name || "Home"}</span>
                                <span className="text-heat">Rows: {game?.away_name || "Away"}</span>
                            </div>
                            <div className="flex justify-center">
                                <SquaresGrid digits={board?.digits} boxes={boxes} meId={meId} onToggle={toggleBox} busyBox={busyBox} locked={locked || mode === "auto"} />
                            </div>
                        </div>
                    )}

                    {locked && checkpointsData && (
                        <div className="rounded-lg border border-white/10 bg-ink p-3" data-testid="squares-checkpoints-card">
                            <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">Live Results</div>
                            <div className="space-y-1.5">
                                {Object.entries(checkpointsData.checkpoints || {}).map(([cp, r]) => (
                                    <div key={cp} className="flex items-start justify-between gap-2 text-sm" data-testid={`squares-checkpoint-${cp}`}>
                                        <span className="uppercase text-[11px] font-bold text-zinc-400 shrink-0 pt-0.5">{cp === "final" ? "Final" : cp.toUpperCase()}</span>
                                        {!r.ready ? (
                                            <span className="text-zinc-400 text-xs">Pending</span>
                                        ) : (
                                            <span className={`text-xs font-bold text-right ${r.participant_id === meId ? "text-volt" : "text-zinc-300"}`}>
                                                {game?.away_name || "Away"} {r.away_score} – {game?.home_name || "Home"} {r.home_score} → {r.username ? `${r.username}${r.participant_id === meId ? " (You!)" : ""}` : "unclaimed box"}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {checkpointsData.finalized && myState === "confirmed" && (
                                <div className="mt-2 pt-2 border-t border-white/10 text-sm font-bold text-volt" data-testid="squares-my-total-award">
                                    You won {(checkpointsData.ledger || []).filter((a) => a.participant_id === meId).reduce((s, a) => s + a.tokens_awarded, 0)} tokens total
                                </div>
                            )}
                        </div>
                    )}

                    {checkpointsData?.finalized && (
                        <div className="rounded-lg border border-volt/30 bg-volt/[0.04] p-3 space-y-3" data-testid="squares-cheer-card">
                            <div className="text-[10px] uppercase tracking-wide text-volt font-bold flex items-center gap-1.5">
                                <PartyPopper className="w-3.5 h-3.5" /> Cheer Section
                            </div>
                            {checkpointsData.cheer?.text ? (
                                <p className="text-[13px] text-zinc-200 leading-relaxed italic" data-testid="squares-cheer-text">
                                    “{checkpointsData.cheer.text}”
                                </p>
                            ) : (
                                <p className="text-[11px] text-zinc-500" data-testid="squares-cheer-pending">
                                    The roast recap is still being written up — check back shortly.
                                </p>
                            )}

                            {leaderboard.length > 0 && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1.5">Leaderboard</div>
                                    <div className="space-y-1">
                                        {leaderboard.map((row, i) => (
                                            <div
                                                key={row.participant_id}
                                                data-testid={`squares-leaderboard-row-${i}`}
                                                className={`flex items-center justify-between text-xs rounded px-2 py-1 ${row.participant_id === meId ? "bg-volt/10 text-volt font-bold" : "text-zinc-300"}`}
                                            >
                                                <span>#{row.rank} {row.username}{row.participant_id === meId ? " (You!)" : ""}</span>
                                                <span>{row.tokens} tokens</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="flex items-center justify-center gap-3 text-[9px] uppercase tracking-wide text-zinc-500 mb-1.5">
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm border-2 border-amber-300" /> Final winner</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm border-2 border-cyan-400" /> Quarter winner</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm ring-1 ring-inset ring-volt/50 bg-volt/10" /> Your box</span>
                                </div>
                                <div className="flex justify-center">
                                    <SquaresGrid digits={board?.digits} boxes={boxes} meId={meId} onToggle={() => {}} busyBox={null} locked winnersByIdx={winnersByIdx} highlightMine={false} testIdPrefix="squares-cheer" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {mode === "manual" && !locked && (
                    <div className="shrink-0 border-t-2 border-[#222] p-3 flex items-center justify-between gap-3" data-testid="squares-modal-footer">
                        <div className="text-sm font-bold" data-testid="squares-footer-counter">
                            <span className={atCap ? "text-green-400" : "text-white"}>{myBoxCount}</span>
                            <span className="text-zinc-500">/</span>
                            <span className={atCap ? "text-green-400" : "text-volt"}>{cap || "∞"}</span>
                            <span className="text-zinc-500 ml-1.5 uppercase text-[10px] tracking-wide">boxes picked</span>
                        </div>
                        <div className="text-sm font-bold text-volt" data-testid="squares-footer-total">
                            {committedTotal} tokens committed
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
